import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Spinner,
  Alert,
  Input,
  FormGroup,
  Label,
  Table,
} from "reactstrap"
import {
  listarCorridas,
  obtenerEstadoCorrida,
  obtenerResultados,
} from "api/migracionApi"
import { getTodasEntidades } from "api/agro"
import TablaImportacion from "./TablaImportacion"
import ResumenPorPasos from "./ResumenPorPasos"
import SeccionActividad from "./SeccionesActividades"
import {
  TarjetaNoImportados,
  TarjetaNumero,
  desglosarNoImportadas,
} from "./TarjetasHistorial"
import {
  derivarResumenPorPasos,
  visibilidadSeccionActividad,
} from "./resumenPasos"
import { proyectarFilasActividad } from "./preparacionActividades"
import {
  GraficoArca,
  GraficoProcesadosPorMotivo,
  GraficoProductores,
} from "./GraficosHistorial"
import { observacionesTexto } from "./categorizacionHistorial"
import {
  clasificarResultado,
  construirMapaEntidades,
  convertirFechasEnTextoART,
  etiquetaCorrida,
  normalizarId,
} from "./migracionEstadoHelpers"
import {
  categoriasProductores,
  coincideObservacion,
  cuitPorProductor,
  datosGraficoArca,
  datosGraficoEstablecimientos,
  OPCIONES_OBSERVACIONES,
  opcionesEntidadFiltro,
  opcionesResultadoFiltro,
  prepararCreadosEstablecimientos,
  prepararFallidosEstablecimientos,
  prepararProcesadosArca,
  prepararProcesadosProductores,
} from "./preparacionHistorial"

// Columnas y campos de busqueda de las tablas: constantes de modulo para que
// no cambien de identidad en cada render (evita refiltrar ~17.000 filas).
// Todo id_* es tipo "id" (sin decimales, alineado a la derecha).
const COLUMNAS_PROD_PROCESADOS = [
  { key: "id_cliente", label: "ID cliente", tipo: "id" },
  { key: "cuit_original", label: "CUIT", tipo: "cuit" },
  { key: "id_productor", label: "ID productor", tipo: "id" },
  { key: "entidad", label: "Entidad", tipo: "texto" },
  { key: "resultado_etiqueta", label: "Resultado", tipo: "texto" },
  { key: "motivo", label: "Motivo", tipo: "motivo", contexto: "productores" },
]
const BUSCAR_PROD_PROCESADOS = ["cuit_original", "id_cliente"]
// BDSJEA-406: listado de productores NO importados (rechazados) con su CUIT.
const COLUMNAS_PROD_NO_IMPORTADOS = [
  { key: "cuit_original", label: "CUIT", tipo: "cuit" },
  { key: "id_cliente", label: "ID cliente", tipo: "id" },
  { key: "entidad", label: "Entidad", tipo: "texto" },
  { key: "resultado_etiqueta", label: "Resultado", tipo: "texto" },
  { key: "motivo", label: "Motivo", tipo: "motivo", contexto: "productores" },
]
const BUSCAR_PROD_NO_IMPORTADOS = ["cuit_original", "id_cliente", "motivo"]
const COLUMNAS_EST_CREADOS = [
  { key: "id_establecimiento_origen", label: "ID origen", tipo: "id" },
  { key: "cuit_productor", label: "CUIT productor", tipo: "cuit" },
  { key: "id_productor", label: "ID productor", tipo: "id" },
  { key: "id_establecimiento", label: "ID establecimiento", tipo: "id" },
  { key: "entidad", label: "Entidad", tipo: "texto" },
  {
    key: "observaciones",
    label: "Observaciones",
    tipo: "texto",
    render: fila => observacionesTexto(fila.observaciones_codigos) || "-",
    valorOrden: fila => observacionesTexto(fila.observaciones_codigos),
  },
]
const BUSCAR_EST_CREADOS = [
  "cuit_productor",
  "id_establecimiento_origen",
  "id_productor",
  "id_establecimiento",
]
const COLUMNAS_EST_FALLIDOS = [
  { key: "id_establecimiento_origen", label: "ID origen", tipo: "id" },
  { key: "id_plan", label: "ID plan", tipo: "id" },
  { key: "cuit_productor", label: "CUIT productor", tipo: "cuit" },
  { key: "id_cliente", label: "ID cliente", tipo: "id" },
  { key: "entidad", label: "Entidad", tipo: "texto" },
  {
    key: "motivo",
    label: "Motivo",
    tipo: "motivo",
    contexto: "establecimientos",
  },
]
const BUSCAR_EST_FALLIDOS = [
  "cuit_productor",
  "id_cliente",
  "id_establecimiento_origen",
  "id_plan",
  "motivo",
]
const COLUMNAS_ARCA_PROCESADOS = [
  { key: "cuit", label: "CUIT", tipo: "cuit" },
  { key: "entidad", label: "Entidad", tipo: "texto" },
  { key: "resultado_etiqueta", label: "Resultado", tipo: "texto" },
  { key: "motivo", label: "Motivo", tipo: "motivo", contexto: "arca" },
]
const BUSCAR_ARCA_PROCESADOS = ["cuit", "motivo"]

const SOLO_DIGITOS = valor => String(valor ?? "").replace(/\D/g, "")

// Entidad por fila para el filtro global: id normalizado o null.
const entidadDeFila = fila => normalizarId(fila?.id_entidad)

// Filas de la entidad elegida ("" = todas). Con una entidad concreta, las filas
// sin entidad resuelta ("Sin entidad") no se muestran.
export function filtrarPorEntidad(filas, entidadSel) {
  if (!entidadSel) return filas
  return filas.filter(f => String(entidadDeFila(f)) === entidadSel)
}

// ARCA: id_entidad de la fila si viene (backend nuevo); si no, join por CUIT
// contra `cuit_original` de Productores. Sin resolver => id_entidad null.
export function enriquecerArcaConEntidad(arca, productores) {
  const porCuit = new Map()
  productores.forEach(p => {
    const id = entidadDeFila(p)
    const cuit = SOLO_DIGITOS(p.cuit_original)
    if (id != null && cuit && !porCuit.has(cuit)) porCuit.set(cuit, id)
  })
  return arca.map(f => {
    const propio = entidadDeFila(f)
    const id = propio != null ? propio : porCuit.get(SOLO_DIGITOS(f.cuit))
    return { ...f, id_entidad: id == null ? null : id }
  })
}

// Filtros comunes: entidad (codigo) y resultado (etiqueta legible).
const filtrosEntidadResultado = (filas, mapaEntidades) => [
  {
    key: "id_entidad",
    label: "Entidad",
    todasLabel: "Todas las entidades",
    opciones: opcionesEntidadFiltro(filas, mapaEntidades),
  },
  {
    key: "resultado",
    label: "Resultado",
    todasLabel: "Todos los resultados",
    opciones: opcionesResultadoFiltro(filas),
  },
]

function SeccionProductores({ productores, mapaEntidades }) {
  const codigosEntidad = useMemo(
    () => Object.values(mapaEntidades || {}),
    [mapaEntidades],
  )
  const categorias = useMemo(
    () => categoriasProductores(productores),
    [productores],
  )
  const creados = useMemo(
    () => productores.filter(f => f.resultado === "CREADO").length,
    [productores],
  )
  const actualizados = useMemo(
    () => productores.filter(f => f.resultado === "ACTUALIZADO").length,
    [productores],
  )
  const yaExistian = useMemo(
    () => productores.filter(f => f.resultado === "YA_EXISTIA").length,
    [productores],
  )
  const procesados = useMemo(
    () => prepararProcesadosProductores(productores, mapaEntidades),
    [productores, mapaEntidades],
  )
  const noImportadas = useMemo(
    () =>
      procesados.filter(
        f => clasificarResultado(f.resultado, "productores").noImportado,
      ),
    [procesados],
  )
  const desglose = useMemo(
    () => desglosarNoImportadas(noImportadas),
    [noImportadas],
  )
  const filtros = useMemo(
    () => filtrosEntidadResultado(procesados, mapaEntidades),
    [procesados, mapaEntidades],
  )
  const filtrosNoImportados = useMemo(
    () => filtrosEntidadResultado(noImportadas, mapaEntidades),
    [noImportadas, mapaEntidades],
  )

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h6">
          <i className="mdi mdi-account-group me-2" />
          Productores
        </CardTitle>
        <Row className="mb-3 align-items-center">
          <Col lg={4} className="mb-3 mb-lg-0">
            <GraficoProductores
              titulo="Distribución de productores"
              categorias={categorias}
            />
          </Col>
          <Col lg={8}>
            <Row className="align-items-start">
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Total"
                  valor={productores.length}
                  color="secondary"
                  testId="prod-total"
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Creados"
                  valor={creados}
                  color="success"
                  testId="prod-creados"
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Actualizados"
                  valor={actualizados}
                  color="primary"
                  testId="prod-actualizados"
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Ya existían"
                  valor={yaExistian}
                  color="warning"
                  testId="prod-ya-existian"
                />
              </Col>
              <Col xs={12} className="mt-2">
                <TarjetaNoImportados
                  total={noImportadas.length}
                  desglose={desglose}
                  testId="prod-no-importados"
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            <p className="text-muted small mb-1">Procesados</p>
            <TablaImportacion
              testId="prod-tabla-procesados"
              titulo="Productores procesados"
              maximizable
              codigosEntidad={codigosEntidad}
              columnas={COLUMNAS_PROD_PROCESADOS}
              filas={procesados}
              buscarEn={BUSCAR_PROD_PROCESADOS}
              placeholderBuscador="Buscar por CUIT o ID cliente"
              filtros={filtros}
              textoVacio="No hay productores procesados."
            />
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            <p className="text-muted small mb-1">No importados</p>
            <TablaImportacion
              testId="prod-tabla-no-importados"
              titulo="Productores no importados"
              maximizable
              codigosEntidad={codigosEntidad}
              columnas={COLUMNAS_PROD_NO_IMPORTADOS}
              filas={noImportadas}
              buscarEn={BUSCAR_PROD_NO_IMPORTADOS}
              placeholderBuscador="Buscar por CUIT, ID cliente o motivo"
              filtros={filtrosNoImportados}
              textoVacio="No hay productores sin importar."
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

function SeccionEstablecimientos({
  establecimientos,
  mapaEntidades,
  mapaCuit,
}) {
  const codigosEntidad = useMemo(
    () => Object.values(mapaEntidades || {}),
    [mapaEntidades],
  )
  const grafico = useMemo(
    () => datosGraficoEstablecimientos(establecimientos),
    [establecimientos],
  )
  const creadas = useMemo(
    () =>
      prepararCreadosEstablecimientos(
        establecimientos.filter(f => f.resultado === "CREADO"),
        mapaCuit,
        mapaEntidades,
      ),
    [establecimientos, mapaCuit, mapaEntidades],
  )
  const actualizados = useMemo(
    () => establecimientos.filter(f => f.resultado === "ACTUALIZADO").length,
    [establecimientos],
  )
  // "Ya existian" = SALTADO_EXISTENTE + YA_EXISTIA, para que Total sea la suma
  // de las tarjetas con cualquier historial.
  const yaExistian = useMemo(
    () =>
      establecimientos.filter(
        f =>
          f.resultado === "SALTADO_EXISTENTE" || f.resultado === "YA_EXISTIA",
      ).length,
    [establecimientos],
  )
  const fallidas = useMemo(
    () =>
      prepararFallidosEstablecimientos(
        establecimientos.filter(
          f => clasificarResultado(f.resultado, "establecimientos").noImportado,
        ),
        mapaEntidades,
        mapaCuit,
      ),
    [establecimientos, mapaEntidades, mapaCuit],
  )
  const desglose = useMemo(() => desglosarNoImportadas(fallidas), [fallidas])
  const filtrosCreados = useMemo(
    () => [
      {
        key: "id_entidad",
        label: "Entidad",
        todasLabel: "Todas las entidades",
        opciones: opcionesEntidadFiltro(creadas, mapaEntidades),
      },
      {
        key: "observaciones_codigos",
        label: "Observaciones",
        todasLabel: "Todas las observaciones",
        opciones: OPCIONES_OBSERVACIONES,
        predicado: coincideObservacion,
      },
    ],
    [creadas, mapaEntidades],
  )
  const filtrosFallidos = useMemo(
    () => filtrosEntidadResultado(fallidas, mapaEntidades),
    [fallidas, mapaEntidades],
  )

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h6">
          <i className="mdi mdi-map-marker-multiple me-2" />
          Establecimientos
        </CardTitle>
        <Row className="mb-3 align-items-center">
          <Col lg={4} className="mb-3 mb-lg-0">
            <GraficoProcesadosPorMotivo
              titulo="Procesados por motivo"
              total={grafico.total}
              creados={grafico.creados}
              noImportados={grafico.noImportados}
              motivos={grafico.motivos}
            />
          </Col>
          <Col lg={8}>
            <Row className="align-items-start">
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Total"
                  valor={establecimientos.length}
                  color="secondary"
                  testId="est-total"
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Creados"
                  valor={creadas.length}
                  color="success"
                  testId="est-creados"
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Actualizados"
                  valor={actualizados}
                  color="primary"
                  testId="est-actualizados"
                />
              </Col>
              <Col xs={6} md={3}>
                <TarjetaNumero
                  etiqueta="Ya existían"
                  valor={yaExistian}
                  color="warning"
                  testId="est-saltados"
                />
              </Col>
              <Col xs={12} className="mt-2">
                <TarjetaNoImportados
                  total={fallidas.length}
                  desglose={desglose}
                  testId="est-no-importados"
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            <p className="text-muted small mb-1">Creados</p>
            <TablaImportacion
              testId="est-tabla-creados"
              titulo="Establecimientos creados"
              maximizable
              codigosEntidad={codigosEntidad}
              columnas={COLUMNAS_EST_CREADOS}
              filas={creadas}
              buscarEn={BUSCAR_EST_CREADOS}
              placeholderBuscador="Buscar por CUIT o ID"
              filtros={filtrosCreados}
              textoVacio="No hay establecimientos creados."
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <p className="text-muted small mb-1">No importados</p>
            <TablaImportacion
              testId="est-tabla-no-importados"
              titulo="Establecimientos no importados"
              maximizable
              codigosEntidad={codigosEntidad}
              columnas={COLUMNAS_EST_FALLIDOS}
              filas={fallidas}
              buscarEn={BUSCAR_EST_FALLIDOS}
              placeholderBuscador="Buscar por CUIT, ID o motivo"
              filtros={filtrosFallidos}
              textoVacio="No hay establecimientos sin importar."
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

function SeccionArca({ arca, mapaEntidades }) {
  const codigosEntidad = useMemo(
    () => Object.values(mapaEntidades || {}),
    [mapaEntidades],
  )
  const grafico = useMemo(() => datosGraficoArca(arca), [arca])
  const sincronizados = useMemo(
    () => arca.filter(f => f.resultado === "SINCRONIZADO").length,
    [arca],
  )
  const sinDatos = useMemo(
    () => arca.filter(f => f.resultado === "SIN_DATOS").length,
    [arca],
  )
  const errores = useMemo(
    () => arca.filter(f => f.resultado === "ERROR").length,
    [arca],
  )
  const procesados = useMemo(
    () => prepararProcesadosArca(arca, mapaEntidades),
    [arca, mapaEntidades],
  )
  const filtros = useMemo(
    () => filtrosEntidadResultado(procesados, mapaEntidades),
    [procesados, mapaEntidades],
  )

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h6">
          <i className="mdi mdi-cloud-sync me-2" />
          Sincronización ARCA
        </CardTitle>
        <Row className="mb-3 align-items-center">
          <Col lg={4} className="mb-3 mb-lg-0">
            <GraficoArca
              titulo="Procesados ARCA"
              total={grafico.total}
              sincronizados={grafico.sincronizados}
              sinDatos={grafico.sinDatos}
              motivos={grafico.motivos}
            />
          </Col>
          <Col lg={8}>
            <Row>
              {[
                ["Total", arca.length, "secondary", "arca-total"],
                [
                  "Sincronizados",
                  sincronizados,
                  "success",
                  "arca-sincronizados",
                ],
                ["Sin datos", sinDatos, "warning", "arca-sin-datos"],
                ["Errores", errores, "danger", "arca-errores"],
              ].map(([l, v, c, t]) => (
                <Col xs={6} md={3} key={l}>
                  <TarjetaNumero etiqueta={l} valor={v} color={c} testId={t} />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
        <Row>
          <Col>
            <p className="text-muted small mb-1">Procesados</p>
            <TablaImportacion
              testId="arca-tabla-procesados"
              titulo="ARCA procesados"
              maximizable
              codigosEntidad={codigosEntidad}
              columnas={COLUMNAS_ARCA_PROCESADOS}
              filas={procesados}
              buscarEn={BUSCAR_ARCA_PROCESADOS}
              placeholderBuscador="Buscar por CUIT o motivo"
              filtros={filtros}
              textoVacio="No hay filas de ARCA."
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

function SeccionTransformaciones({ transformaciones }) {
  const porTipo = Object.entries(
    transformaciones.reduce((acc, f) => {
      const campo = f.campo || "desconocido"
      acc[campo] = (acc[campo] || 0) + 1
      return acc
    }, {}),
  )
    .map(([campo, cantidad]) => ({ campo, cantidad }))
    .sort((a, b) => b.cantidad - a.cantidad)

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h6">
          <i className="mdi mdi-swap-horizontal me-2" />
          Transformaciones aplicadas
          <small className="text-muted fw-normal ms-2">(toda la corrida)</small>
        </CardTitle>
        <Row>
          <Col md={5}>
            <p className="text-muted small">
              Total: <strong>{transformaciones.length}</strong>
            </p>
            <p className="text-muted small mb-1">Top 5 más frecuentes</p>
            <Table size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Campo</th>
                  <th>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {porTipo.slice(0, 5).map((t, i) => (
                  <tr key={i}>
                    <td>{t.campo}</td>
                    <td>{t.cantidad}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

function SeccionResumen({ resumen }) {
  if (!resumen) return null
  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h6">
          <i className="mdi mdi-text-box-outline me-2" />
          Resumen de corrida
          <small className="text-muted fw-normal ms-2">(toda la corrida)</small>
        </CardTitle>
        <pre
          style={{
            fontSize: 12,
            maxHeight: 200,
            overflowY: "auto",
            background: "#f8f9fa",
            padding: 8,
            borderRadius: 4,
          }}
        >
          {convertirFechasEnTextoART(resumen)}
        </pre>
      </CardBody>
    </Card>
  )
}

const DashboardMigracion = ({ corridaInicial }) => {
  const [corridas, setCorridas] = useState([])
  const [corridaSeleccionada, setCorridaSeleccionada] = useState(
    corridaInicial || "",
  )
  const [datos, setDatos] = useState(null)
  // BDSJEA-441: estado de la corrida (pasos, duraciones, parametros.niveles) para
  // el «Resumen por pasos». null = no se pudo leer: la sección no se muestra y
  // el resto de la pantalla sigue igual.
  const [estadoCorrida, setEstadoCorrida] = useState(null)
  const ultimaCargaRef = useRef(0)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  // {id: codigo} del catalogo de entidades; vacio si no se pudo cargar
  // (entonces los mensajes quedan con el numero de entidad).
  const [mapaEntidades, setMapaEntidades] = useState({})
  // Filtro global por entidad ("" = Todas, si no el id como texto). Se conserva
  // al cambiar de corrida: quien compara corridas suele seguir mirando la
  // misma entidad.
  const [entidadSel, setEntidadSel] = useState("")

  useEffect(() => {
    let activo = true
    getTodasEntidades()
      .then(lista => {
        if (activo) setMapaEntidades(construirMapaEntidades(lista))
      })
      .catch(() => {})
    return () => {
      activo = false
    }
  }, [])

  useEffect(() => {
    listarCorridas()
      .then(lista => {
        setCorridas(lista)
        if (!corridaSeleccionada && lista.length > 0) {
          setCorridaSeleccionada(lista[0])
        }
      })
      .catch(() => setError("No se pudieron cargar las corridas anteriores"))
  }, [])

  const cargarDatos = useCallback(corrida => {
    if (!corrida) return
    const carga = ++ultimaCargaRef.current
    setCargando(true)
    setError(null)
    // El estado es complementario: cualquier falla (incluida una API vieja) lo
    // deja en null y no afecta a los resultados.
    const pEstado = (async () => {
      try {
        const e = await obtenerEstadoCorrida(corrida)
        return e && typeof e === "object" ? e : null
      } catch {
        return null
      }
    })()
    obtenerResultados(corrida)
      .then(d => {
        if (carga !== ultimaCargaRef.current) return
        // BDSJEA-441: los resultados se muestran apenas llegan, sin esperar a
        // /estado; el estado (complementario) se completa cuando llegue.
        setEstadoCorrida(null)
        setDatos(d)
        setCargando(false)
        pEstado.then(e => {
          if (carga !== ultimaCargaRef.current) return
          setEstadoCorrida(e)
        })
      })
      .catch(() => {
        if (carga !== ultimaCargaRef.current) return
        setError("Error al cargar los resultados de la corrida")
        setCargando(false)
      })
  }, [])

  useEffect(() => {
    if (corridaInicial) {
      setCorridaSeleccionada(corridaInicial)
      cargarDatos(corridaInicial)
    }
  }, [corridaInicial, cargarDatos])

  useEffect(() => {
    if (corridaSeleccionada && corridaSeleccionada !== corridaInicial) {
      cargarDatos(corridaSeleccionada)
    }
  }, [corridaSeleccionada, corridaInicial, cargarDatos])

  const arcaConEntidad = useMemo(
    () => enriquecerArcaConEntidad(datos?.arca || [], datos?.productores || []),
    [datos],
  )
  const productoresF = useMemo(
    () => filtrarPorEntidad(datos?.productores || [], entidadSel),
    [datos, entidadSel],
  )
  const establecimientosF = useMemo(
    () => filtrarPorEntidad(datos?.establecimientos || [], entidadSel),
    [datos, entidadSel],
  )
  // BDSJEA-441: filas de las actividades (solo columnas de la lista blanca),
  // filtradas por la entidad elegida. Corridas viejas: claves ausentes => [].
  const siembrasF = useMemo(
    () =>
      filtrarPorEntidad(
        proyectarFilasActividad(datos?.siembras, "siembras"),
        entidadSel,
      ),
    [datos, entidadSel],
  )
  const ganaderiaF = useMemo(
    () =>
      filtrarPorEntidad(
        proyectarFilasActividad(datos?.ganaderia, "ganaderia"),
        entidadSel,
      ),
    [datos, entidadSel],
  )
  const tamboF = useMemo(
    () =>
      filtrarPorEntidad(
        proyectarFilasActividad(datos?.tambo, "tambo"),
        entidadSel,
      ),
    [datos, entidadSel],
  )
  const stockAgricolaF = useMemo(
    () =>
      filtrarPorEntidad(
        proyectarFilasActividad(datos?.stockAgricola, "stockAgricola"),
        entidadSel,
      ),
    [datos, entidadSel],
  )
  const resumenFilas = useMemo(
    () => derivarResumenPorPasos(estadoCorrida),
    [estadoCorrida],
  )
  const actividades = [
    ["siembras", siembrasF, datos?.siembras],
    ["ganaderia", ganaderiaF, datos?.ganaderia],
    ["tambo", tamboF, datos?.tambo],
    ["stockAgricola", stockAgricolaF, datos?.stockAgricola],
  ]
  // CUIT de cada productor (respaldo para establecimientos de corridas viejas);
  // sobre TODOS los productores: no depende del combo de entidad.
  const mapaCuit = useMemo(() => cuitPorProductor(datos?.productores), [datos])
  const arcaF = useMemo(
    () => filtrarPorEntidad(arcaConEntidad, entidadSel),
    [arcaConEntidad, entidadSel],
  )
  // Entidades del catalogo + las que aparecen en los datos (y la elegida);
  // etiqueta = codigo, o el id numerico si el catalogo no cargo.
  const opcionesEntidades = useMemo(() => {
    const ids = new Set(Object.keys(mapaEntidades).map(Number))
    ;[
      ...(datos?.productores || []),
      ...(datos?.establecimientos || []),
      ...arcaConEntidad,
      ...(Array.isArray(datos?.siembras) ? datos.siembras : []),
      ...(Array.isArray(datos?.ganaderia) ? datos.ganaderia : []),
      ...(Array.isArray(datos?.tambo) ? datos.tambo : []),
      ...(Array.isArray(datos?.stockAgricola) ? datos.stockAgricola : []),
    ].forEach(f => {
      const id = entidadDeFila(f)
      if (id != null) ids.add(id)
    })
    if (entidadSel) ids.add(Number(entidadSel))
    return [...ids]
      .sort((a, b) => a - b)
      .map(id => ({
        valor: String(id),
        etiqueta: mapaEntidades[id] || String(id),
      }))
  }, [mapaEntidades, datos, arcaConEntidad, entidadSel])

  return (
    <div>
      <FormGroup row className="align-items-center mb-3">
        <Label sm={3} className="text-muted small">
          Corrida:
        </Label>
        <Col sm={9}>
          <Input
            type="select"
            bsSize="sm"
            value={corridaSeleccionada}
            onChange={e => setCorridaSeleccionada(e.target.value)}
          >
            <option value="">-- Seleccionar corrida --</option>
            {corridas.map(c => (
              <option key={c} value={c}>
                {etiquetaCorrida(c)}
              </option>
            ))}
          </Input>
        </Col>
      </FormGroup>
      <FormGroup row className="align-items-center mb-3">
        <Label sm={3} for="historial-entidad" className="text-muted small">
          Por entidad:
        </Label>
        <Col sm={9}>
          <Input
            id="historial-entidad"
            type="select"
            bsSize="sm"
            value={entidadSel}
            data-testid="historial-entidad"
            aria-label="Filtrar todo el historial por entidad"
            onChange={e => setEntidadSel(e.target.value)}
          >
            <option value="">Todas</option>
            {opcionesEntidades.map(op => (
              <option key={op.valor} value={op.valor}>
                {op.etiqueta}
              </option>
            ))}
          </Input>
        </Col>
      </FormGroup>

      {error && (
        <Alert color="warning" fade={false}>
          {error}
        </Alert>
      )}

      {cargando && (
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="text-muted mt-2">Cargando resultados...</p>
        </div>
      )}

      {!cargando && datos && (
        <>
          <ResumenPorPasos filas={resumenFilas} />
          {datos.productores?.length > 0 && (
            <SeccionProductores
              productores={productoresF}
              mapaEntidades={mapaEntidades}
            />
          )}
          {datos.establecimientos?.length > 0 && (
            <SeccionEstablecimientos
              establecimientos={establecimientosF}
              mapaEntidades={mapaEntidades}
              mapaCuit={mapaCuit}
            />
          )}
          {datos.arca?.length > 0 && (
            <SeccionArca arca={arcaF} mapaEntidades={mapaEntidades} />
          )}
          {actividades.map(([nivel, filas, crudas]) => {
            const vis = visibilidadSeccionActividad(nivel, resumenFilas, crudas)
            if (!vis) return null
            return (
              <SeccionActividad
                key={nivel}
                nivel={nivel}
                filas={filas}
                mapaEntidades={mapaEntidades}
                noEjecutado={vis.noEjecutado}
              />
            )
          })}
          {datos.transformaciones?.length > 0 && (
            <SeccionTransformaciones
              transformaciones={datos.transformaciones}
            />
          )}
          <SeccionResumen resumen={datos.resumen} />
        </>
      )}

      {!cargando && !datos && !error && (
        <Alert color="light" fade={false} className="text-center">
          Seleccioná una corrida para ver el dashboard.
        </Alert>
      )}
    </div>
  )
}

export default DashboardMigracion
